import React, { useMemo, useCallback } from "react";
import { Modal, ModalFooter, ModalContent, ModalHeader, Button, Text, CodeSnippet, SubTable, Popover, useTable, Tooltip } from "@netapp/bxp-design-system-react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { allMenus as i18nNamespace } from "../../../../../../i18n/namespace";
import userTokenStyles from "./UserTokenModal.module.scss";
import { base64UrlDecode } from "utils/app/utils";
import _ from "lodash";

const decodeJWT = (token) => {
    try {
        if (!token) return null;

        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error('Invalid JWT token format');
            return null;
        }

        const payload = JSON.parse(base64UrlDecode(parts[1]));

        return payload;
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
};

const GroupNameCell = ({ row }) => (
    <div className={userTokenStyles['group-name-cell']}>
        {row.name}
        <Tooltip>{row.name}</Tooltip>
    </div>
);

const TABLE_COLUMNS = [{
    accessor: 'name',
    id: 'name',
    Renderer: GroupNameCell,
}];

const FederatedGroupsListTable = React.memo(({ fedGroups }) => {
    const tableRows = useMemo(() => 
        _.drop(fedGroups, 1).map(groupName => ({ name: groupName })),
        [fedGroups]
    );
    
    const { organizedRows, columns } = useTable({ columns: TABLE_COLUMNS, rows: tableRows });
    
    const handleCapture = useCallback((e) => {
        e.stopPropagation();
    }, []);
    
    return (
        <div onClickCapture={handleCapture}>
            <SubTable 
                headerContainerClassName={userTokenStyles['header-row-hidden']} 
                columns={columns} 
                rows={organizedRows}
            />
        </div>
    );
});

export default ({ setShowUserTokenModal }) => {
    const accessToken = useSelector(state => state.auth.accessToken);
    const { t } = useTranslation(i18nNamespace);

    const { tokenInfo, fedGroups } = useMemo(() => {
        const decoded = decodeJWT(accessToken);
        if (!decoded) {
            return { tokenInfo: '', fedGroups: null };
        }
        return {
            tokenInfo: JSON.stringify(decoded, null, 2),
            fedGroups: _.get(decoded, 'http://cloud.netapp.com/groups', null)
        };
    }, [accessToken]);

    const closeTokenModal = useCallback(() => {
        setShowUserTokenModal(false);
    }, [setShowUserTokenModal]);

    const additionalGroupsCount = !_.isEmpty(fedGroups) ? fedGroups.length - 1 : 0;

    return (
        <Modal>
            <ModalHeader>{t('user_token')}</ModalHeader>
            <ModalContent>
                {!_.isEmpty(fedGroups) && (
                    <Text className={userTokenStyles['user-token-text']}>
                        {t("user_token_ad_groups_message")} "{fedGroups[0]}"{additionalGroupsCount > 0 && (
                            <>
                                {t('and')}
                                <Popover 
                                    placement='center' 
                                    verticalPlacement='bottom' 
                                    contentClassName={userTokenStyles['popover-content-style']} 
                                    Trigger={() => <Button variant="text">{additionalGroupsCount}</Button>}
                                >
                                    <FederatedGroupsListTable fedGroups={fedGroups} />
                                </Popover>
                            </>
                        )} {additionalGroupsCount > 0 ? t("federated_groups_small") : t("federated_group_small")}
                    </Text>
                )}
                <CodeSnippet text={tokenInfo} />
            </ModalContent>
            <ModalFooter>
                <Button dataTestId="close-user-token-button" onClick={closeTokenModal}>
                    {t('close')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};